import React, { useEffect, useMemo, useState } from 'react'
import { ReloadOutlined, SearchOutlined } from '@ant-design/icons'
import { Alert, Button, Card, Flex, Input, Select, Typography } from 'antd'
import { useUnit } from 'effector-react'
import { GUARD_RULES } from 'src/features/session/constants'
import { Guard } from 'src/features/session/guard'
import { useUrlFilters } from '../../../libs/useUrlFilters'
import {
  $pendingWalletId,
  $wallets,
  $walletsHasError,
  $walletsTotal,
  getWalletsFx,
  walletBlockRequested,
  walletsRequested,
  walletUnblockRequested,
} from '../model'
import { WalletGuardWalletsRequest } from '../types'
import {
  FILTER_CONFIG,
  WALLET_CURRENCIES,
  WALLET_SORT_FIELDS,
  WALLET_STATUS_LABELS,
  WALLET_TYPE_LABELS,
} from './config'
import { WalletsTable } from './WalletsTable'
import { CreateWalletForm } from './create/CreateWalletForm'

export const WalletGuardWalletsPage = () => {
  const {
    wallets,
    total,
    loading,
    hasError,
    requestWallets,
    pendingWalletId,
    blockWallet,
    unblockWallet,
  } = useUnit({
    wallets: $wallets,
    total: $walletsTotal,
    loading: getWalletsFx.pending,
    hasError: $walletsHasError,
    requestWallets: walletsRequested,
    pendingWalletId: $pendingWalletId,
    blockWallet: walletBlockRequested,
    unblockWallet: walletUnblockRequested,
  })
  const { filters, setFilters, hasActiveFilters } = useUrlFilters(FILTER_CONFIG)
  const [searchInputs, setSearchInputs] = useState({
    address: filters.address ?? '',
    name: filters.name ?? '',
  })

  useEffect(() => {
    setSearchInputs({
      address: filters.address ?? '',
      name: filters.name ?? '',
    })
  }, [filters.address, filters.name])

  const options = useMemo(() => {
    const valuesFor = (field: 'currency' | 'type' | 'status') => {
      const labels =
        field === 'type'
          ? WALLET_TYPE_LABELS
          : field === 'status'
          ? WALLET_STATUS_LABELS
          : {}

      const knownValues =
        field === 'currency' ? WALLET_CURRENCIES : Object.keys(labels)

      return Array.from(
        new Set([
          ...knownValues,
          ...(wallets?.map((wallet) => wallet[field]) ?? []),
          ...(filters[field] ?? []),
        ]),
      )
        .map((value) => ({ value, label: labels[value] ?? value }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ru'))
    }

    return {
      currency: valuesFor('currency'),
      type: valuesFor('type'),
      status: valuesFor('status'),
    }
  }, [wallets, filters])

  const requestedPageSize = Number(filters.pageSize)
  const pageSize = [10, 20, 50, 100].includes(requestedPageSize)
    ? requestedPageSize
    : 10
  const requestedPage = Number(filters.page)
  const page =
    Number.isSafeInteger(requestedPage) && requestedPage > 0 ? requestedPage : 1
  const orderBy = WALLET_SORT_FIELDS.find(
    (field) => field === filters.sortField,
  )
  const orderDirection =
    filters.sortOrder === 'ascend' || filters.sortOrder === 'descend'
      ? filters.sortOrder
      : undefined

  const requestParams = useMemo<WalletGuardWalletsRequest>(
    () => ({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      filters: {
        address: filters.address,
        name: filters.name,
        currency: filters.currency,
        type: filters.type,
        status: filters.status,
      },
    }),
    [pageSize, page, orderBy, orderDirection, filters],
  )

  useEffect(() => {
    requestWallets(requestParams)
  }, [requestWallets, requestParams])

  const applySearch = (key: 'address' | 'name', value: string) => {
    const search = value.trim()
    if (search !== (filters[key] ?? '')) {
      setFilters({ [key]: search || undefined, page: undefined })
    }
  }

  return (
    <Flex vertical gap={24}>
      <Typography.Title level={1} style={{ margin: 0 }}>
        Кошельки
      </Typography.Title>

      <Guard permission={GUARD_RULES.EDIT_CRYPTO_WALLET} mode="permission">
        <CreateWalletForm />
      </Guard>

      <Card size="small">
        <Flex gap={12} wrap="wrap" align="end" aria-label="Фильтры кошельков">
          {(
            [
              { key: 'address', label: 'Адрес' },
              { key: 'name', label: 'Название' },
            ] as const
          ).map(({ key, label }) => (
            <Flex key={key} vertical gap={4} flex="1 1 230px">
              <Typography.Text>{label}</Typography.Text>
              <Input
                aria-label={label}
                placeholder={`Поиск: ${label.toLowerCase()}`}
                prefix={<SearchOutlined />}
                allowClear
                value={searchInputs[key]}
                onChange={(event) =>
                  setSearchInputs((values) => ({
                    ...values,
                    [key]: event.target.value,
                  }))
                }
                onPressEnter={() => applySearch(key, searchInputs[key])}
                onBlur={() => applySearch(key, searchInputs[key])}
                onClear={() => applySearch(key, '')}
              />
            </Flex>
          ))}

          {(
            [
              { key: 'currency', label: 'Валюта' },
              { key: 'type', label: 'Тип' },
              { key: 'status', label: 'Статус' },
            ] as const
          ).map(({ key, label }) => (
            <Flex
              key={key}
              vertical
              gap={4}
              flex="1 1 180px"
              style={{ minWidth: 0 }}
            >
              <Typography.Text>{label}</Typography.Text>
              <Select
                aria-label={label}
                mode="multiple"
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Все"
                maxTagCount="responsive"
                value={filters[key] ?? []}
                options={options[key]}
                onChange={(values: string[]) =>
                  setFilters({ [key]: values, page: undefined })
                }
              />
            </Flex>
          ))}

          <Button
            icon={<ReloadOutlined />}
            disabled={
              !hasActiveFilters && !searchInputs.address && !searchInputs.name
            }
            onClick={() => {
              setSearchInputs({ address: '', name: '' })
              setFilters({
                address: undefined,
                name: undefined,
                currency: undefined,
                type: undefined,
                status: undefined,
                page: undefined,
              })
            }}
          >
            Сбросить фильтры
          </Button>
        </Flex>
      </Card>

      {hasError ? (
        <Alert
          type="error"
          showIcon
          message="Не удалось загрузить кошельки"
          action={
            <Button onClick={() => requestWallets(requestParams)}>
              Повторить
            </Button>
          }
        />
      ) : (
        <WalletsTable
          wallets={wallets ?? []}
          total={total}
          loading={loading || wallets === null}
          page={page}
          pageSize={pageSize}
          sortField={orderBy}
          sortOrder={orderDirection}
          pendingWalletId={pendingWalletId}
          onBlock={blockWallet}
          onUnblock={unblockWallet}
          onChange={(pagination, _tableFilters, sorter, extra) => {
            if (extra.action === 'sort' && !Array.isArray(sorter)) {
              setFilters({
                sortField: sorter.order ? String(sorter.columnKey) : undefined,
                sortOrder: sorter.order ?? undefined,
                page: undefined,
              })
            } else if (extra.action === 'paginate') {
              setFilters({
                page:
                  pagination.pageSize !== pageSize
                    ? undefined
                    : String(pagination.current ?? 1),
                pageSize: String(pagination.pageSize ?? pageSize),
              })
            }
          }}
        />
      )}
    </Flex>
  )
}
